import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { alpha } from '@mui/material/styles';
import {
  Autocomplete,
  FormHelperText,
  IconButton,
  ListItemText,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';

import axiosInstance from 'src/utils/axios';

import { useAppSelector } from 'src/redux/store';
import { TCreateStationeryDispatch } from 'src/redux/slices/stationery-dispatch';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';

import { IEmployeeListItem } from 'src/types/employee';
import { IStationeryItemListItem } from 'src/types/stationery-item';
import { IStationeryInsufficientInventoryItem } from 'src/types/stationery-dispatch';

interface Props {
  index: number;
  remove: (index: number) => void;
  isLast: boolean;
  // Seeds the ad-hoc search results with the line's already-selected item
  // (edit mode only) — otherwise the Autocomplete has no way to resolve a
  // display name for it before the user types a search query.
  initialItem?: IStationeryItemListItem;
  // Keyed by stationeryItem id — set from the last failed submit's
  // insufficient-inventory response. Every line referencing a flagged
  // item is highlighted, not just one, since the shortfall is the
  // *combined* quantity across duplicate lines.
  insufficientItemsByStationeryItem?: Map<string, IStationeryInsufficientInventoryItem>;
}

export default function StationeryDispatchNewItemRow({
  index,
  remove,
  isLast,
  initialItem,
  insufficientItemsByStationeryItem,
}: Props) {
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [localItems, setLocalItems] = useState<IStationeryItemListItem[]>(
    initialItem ? [initialItem] : []
  );

  const { employees } = useAppSelector((state) => state.employees);

  const {
    control,
    formState: { errors },
  } = useFormContext<TCreateStationeryDispatch>();

  const defaultHelperText: string = errors.items?.[index] ? ' ' : '';

  // Existing lines (identified by a submitted `_id`) can't swap their
  // stationeryItem on edit — a Return line may already reference this
  // dispatch item's id, so the backend silently keeps the original
  // stationeryItem for any line carrying an `_id` regardless of what's
  // submitted. Locking it here avoids a misleading "successful" edit that
  // the server then ignores.
  const existingItemId = useWatch({ control, name: `items.${index}._id` as any });
  const isExistingLine = !!existingItemId;

  const watchedStationeryItemId = useWatch({
    control,
    name: `items.${index}.stationeryItem` as any,
  });
  const insufficientItem = insufficientItemsByStationeryItem?.get(
    typeof watchedStationeryItemId === 'string'
      ? watchedStationeryItemId
      : (watchedStationeryItemId as IStationeryItemListItem | undefined)?._id ?? ''
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedStationeryItems = useCallback(
    debounce(async (query) => {
      try {
        const response = await axiosInstance.get(
          `stationery/item?page=&limit=&generalSearch=${query}&itemName=&itemCode=&minQuantity=&maxQuantity=&unit=&alertEnabled=&status=active&sortBy=itemName&sort=asc`
        );

        setLocalItems(response.data.data.items);
      } catch (err) {
        console.warn(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchPaginatedStationeryItems(itemSearchQuery);

    return fetchPaginatedStationeryItems.cancel;
  }, [itemSearchQuery, fetchPaginatedStationeryItems]);

  return (
    <TableRow
      sx={
        insufficientItem
          ? { bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) }
          : undefined
      }
    >
      <TableCell>
        {index + 1 < 10 ? `0${index + 1}` : index + 1}

        <FormHelperText>{defaultHelperText}</FormHelperText>
      </TableCell>

      <TableCell sx={{ minWidth: 280 }}>
        {isExistingLine ? (
          <ListItemText
            primary={initialItem?.itemName}
            secondary={initialItem ? `${initialItem.itemCode} — cannot be changed` : undefined}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
          />
        ) : (
          <Controller
            name={`items.${index}.stationeryItem`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                fullWidth
                size="small"
                multiple={false}
                options={localItems}
                isOptionEqualToValue={(
                  option: IStationeryItemListItem | string,
                  value: IStationeryItemListItem | string
                ) => {
                  const optionId = typeof option === 'string' ? option : option?._id;
                  const valueId = typeof value === 'string' ? value : value?._id;
                  return optionId === valueId;
                }}
                getOptionLabel={(option: IStationeryItemListItem | string) => {
                  if (!option) return '';
                  if (typeof option === 'string') {
                    if (option.length === 0) return option;
                    const value = localItems.find(
                      (item: IStationeryItemListItem) => item._id === option
                    );
                    return value ? value.itemName : '';
                  }
                  return option.itemName;
                }}
                renderOption={(props, option: IStationeryItemListItem | string) => {
                  if (typeof option === 'string') {
                    return (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option._id}>
                      {option.itemName} ({option.itemCode})
                    </li>
                  );
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') setItemSearchQuery(value);
                  if (reason === 'clear') setItemSearchQuery('');
                }}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    required
                    helperText={error ? error.message : defaultHelperText}
                    {...params}
                  />
                )}
                onChange={(event, newValue) => {
                  field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
                }}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 120 }}>
        <RHFTextField
          fullWidth
          required
          type="number"
          size="small"
          name={`items.${index}.quantity`}
          helperText={
            insufficientItem
              ? `Only ${insufficientItem.availableQuantity} available`
              : defaultHelperText
          }
          FormHelperTextProps={insufficientItem ? { sx: { color: 'error.main' } } : undefined}
          showError={false}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 220 }}>
        <Controller
          name={`items.${index}.receivedBy`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              options={employees.employees}
              isOptionEqualToValue={(
                option: IEmployeeListItem | string,
                value: IEmployeeListItem | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IEmployeeListItem | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = employees.employees.find(
                    (employee: IEmployeeListItem) => employee._id === option
                  );
                  return value
                    ? `${value.firstName}${value.lastName ? ` ${value.lastName}` : ''} (${value.employeeNumber})`
                    : '';
                }
                return `${option.firstName}${option.lastName ? ` ${option.lastName}` : ''} (${option.employeeNumber})`;
              }}
              renderOption={(props, option: IEmployeeListItem | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.firstName}
                    {option.lastName ? ` ${option.lastName}` : ''} ({option.employeeNumber})
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  label="Received By"
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      <TableCell align="right">
        <Tooltip title={isLast ? 'Last item cannot be deleted' : 'Delete'}>
          <span>
            <IconButton disabled={isLast} color="error" onClick={() => remove(index)}>
              <Iconify icon="solar:trash-bin-trash-bold" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
